import { ref, computed, readonly } from 'vue'
import { apiFetch } from '@/store/api'
import {
  dbGet, dbSet, clearAllOfflineData,
  getPendingReports, removePendingReport
} from '@/utils/offlineDB'

// ── Persisted flags (localStorage) ───────────────────────────────────────
const LS_ENABLED   = 'offlineMode'
const LS_LAST_ONLINE = 'offlineLastOnline'
const LS_TIMEOUT_DAYS = 'offlineTimeoutDays'

export function isOfflineModeEnabled(): boolean {
  return localStorage.getItem(LS_ENABLED) === '1'
}

function setOfflineModeEnabled(v: boolean) {
  localStorage.setItem(LS_ENABLED, v ? '1' : '0')
}

export function getLastOnlineAt(): string {
  return localStorage.getItem(LS_LAST_ONLINE) || ''
}

function setLastOnlineAt(iso: string) {
  localStorage.setItem(LS_LAST_ONLINE, iso)
}

export function getOfflineTimeoutDays(): number {
  const v = localStorage.getItem(LS_TIMEOUT_DAYS)
  return v != null ? Number(v) : 30
}

function setOfflineTimeoutDays(days: number) {
  localStorage.setItem(LS_TIMEOUT_DAYS, String(days))
}

// ── Reactive state ────────────────────────────────────────────────────────
const _enabled       = ref(isOfflineModeEnabled())
const _isOffline     = ref(!navigator.onLine)
const _syncing       = ref(false)
const _downloading   = ref(false)
const _downloadPct   = ref(0)
const _pendingCount  = ref(0)
const _lastOnlineAt  = ref(getLastOnlineAt())
const _blocked       = ref(false)   // timeout exceeded while offline

window.addEventListener('online',  () => { _isOffline.value = false })
window.addEventListener('offline', () => { _isOffline.value = true  })

// ── Computed ──────────────────────────────────────────────────────────────
export const offlineModeEnabled  = readonly(_enabled)
export const isOffline           = readonly(_isOffline)
export const offlineSyncing      = readonly(_syncing)
export const offlineDownloading  = readonly(_downloading)
export const offlineDownloadPct  = readonly(_downloadPct)
export const offlinePendingCount = readonly(_pendingCount)
export const offlineLastOnlineAt = readonly(_lastOnlineAt)
export const offlineBlocked      = readonly(_blocked)

export const daysOffline = computed(() => {
  const last = _lastOnlineAt.value
  if (!last) return 0
  const ms = Date.now() - new Date(last).getTime()
  return Math.floor(ms / 86_400_000)
})

export const daysUntilBlock = computed(() => {
  const timeout = getOfflineTimeoutDays()
  if (timeout === 0) return null   // unlimited
  return Math.max(0, timeout - daysOffline.value)
})

// ── Offline timeout check ─────────────────────────────────────────────────
export function checkOfflineTimeout(): boolean {
  if (!_isOffline.value) return false          // online → never blocked
  if (!_enabled.value) return false            // offline mode not enabled
  const timeout = getOfflineTimeoutDays()
  if (timeout === 0) return false              // unlimited
  const blocked = daysOffline.value >= timeout
  _blocked.value = blocked
  return blocked
}

// ── Heartbeat ─────────────────────────────────────────────────────────────
export async function sendHeartbeat(): Promise<void> {
  if (_isOffline.value) return
  try {
    const { lastOnlineAt } = await apiFetch<{ ok: boolean; lastOnlineAt: string }>('/heartbeat', { method: 'POST' })
    setLastOnlineAt(lastOnlineAt)
    _lastOnlineAt.value = lastOnlineAt
    _blocked.value = false
  } catch {
    // ignore – best effort
  }
}

// ── Enable offline mode (download bundle) ─────────────────────────────────
export async function enableOfflineMode(): Promise<void> {
  if (_downloading.value) return
  _downloading.value = true
  _downloadPct.value = 5

  try {
    // 1. Fetch the bundle
    const bundle = await apiFetch<{
      bundledAt: string
      projects: unknown[]
      submissions: unknown[]
      systemConfig: Record<string, unknown>
      accounts: unknown[]
      roles: unknown[]
    }>('/offline-bundle')

    _downloadPct.value = 60

    // 2. Persist to IndexedDB
    await Promise.all([
      dbSet('bundle:projects',    bundle.projects),
      dbSet('bundle:submissions', bundle.submissions),
      dbSet('bundle:systemConfig', bundle.systemConfig),
      dbSet('bundle:accounts',    bundle.accounts),
      dbSet('bundle:roles',       bundle.roles),
      dbSet('bundle:bundledAt',   bundle.bundledAt),
    ])

    _downloadPct.value = 90

    // 3. Store timeout from config
    const timeout = bundle.systemConfig.offlineTimeoutDays
    if (typeof timeout === 'number') setOfflineTimeoutDays(timeout)

    // 4. Record last online
    await sendHeartbeat()

    // 5. Activate
    setOfflineModeEnabled(true)
    _enabled.value = true
    _downloadPct.value = 100
  } finally {
    setTimeout(() => { _downloading.value = false; _downloadPct.value = 0 }, 600)
  }
}

// ── Disable offline mode ──────────────────────────────────────────────────
export async function disableOfflineMode(): Promise<void> {
  await clearAllOfflineData()
  setOfflineModeEnabled(false)
  _enabled.value = false
  _pendingCount.value = 0
}

// ── Get cached bundle data ─────────────────────────────────────────────────
export async function getCachedBundle<T>(key: string): Promise<T | undefined> {
  return dbGet<T>(`bundle:${key}`)
}

// ── Sync pending reports when back online ────────────────────────────────
export async function syncPendingReports(): Promise<void> {
  if (_syncing.value || _isOffline.value) return
  const reports = await getPendingReports()
  if (!reports.length) return

  _syncing.value = true
  try {
    for (const report of reports) {
      try {
        await apiFetch('/submissions', {
          method: 'POST',
          body: JSON.stringify(report.payload)
        })
        await removePendingReport(report.id)
      } catch {
        // leave in queue for next attempt
      }
    }
    await refreshPendingCount()
  } finally {
    _syncing.value = false
  }
}

// ── Refresh pending count ─────────────────────────────────────────────────
export async function refreshPendingCount(): Promise<void> {
  const reports = await getPendingReports()
  _pendingCount.value = reports.length
}

// Initialise count on load
refreshPendingCount().catch(() => null)
